/**
 * O seletor de prioridade da conversa.
 *
 * ── Por que ele deixou de ser só um rótulo ───────────────────────────────
 *
 * A prioridade aparecia na tela e não se mudava, porque não havia rota. Quem
 * precisava subir uma conversa para urgente ia ao motor de conversa por fora —
 * e a partir do momento em que uma ação do dia a dia mora noutro produto, o
 * CliqCRM deixa de ser o lugar onde o atendimento acontece.
 *
 * ── Otimismo é PROIBIDO aqui ─────────────────────────────────────────────
 *
 * O motor aceita a prioridade e pode não aplicá-la: `toggle_priority` responde
 * 200 sem olhar se o `save` deu certo, e a API relê antes de responder. Por
 * isso o seletor NÃO pinta o valor escolhido enquanto grava — ele fica
 * desabilitado e diz "salvando…".
 *
 * Pintar o escolhido e corrigir depois seria a §11.1 ao contrário: o indicador
 * fica verde e a pessoa segue achando que classificou. E prioridade errada não
 * dá sinal nenhum — o erro só aparece no dia em que alguém filtra por urgente
 * e a conversa não está lá.
 *
 * ── "Nenhuma" é uma opção, e não o vazio do seletor ──────────────────────
 *
 * Conversa sem degrau é o estado mais comum de todos. Sem essa opção na lista,
 * um urgente posto por engano não teria como voltar atrás pela tela.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ConversaComposta } from '@cliqcrm/contracts';
import { useApi } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { CampoEscolha } from '@/shared/ui';

type Prioridade = ConversaComposta['conversa']['prioridade'];

/** O valor do contrato é minúsculo e sem acento; `media` lido cru é outra palavra. */
const OPCOES: readonly { valor: string; rotulo: string }[] = [
  { valor: '', rotulo: 'Nenhuma' },
  { valor: 'baixa', rotulo: 'Baixa' },
  { valor: 'media', rotulo: 'Média' },
  { valor: 'alta', rotulo: 'Alta' },
  { valor: 'urgente', rotulo: 'Urgente' },
];

export function SeletorDePrioridade({
  conversaId,
  prioridade,
  rotuloOculto = false,
}: {
  conversaId: string;
  prioridade: Prioridade;
  /** Só para o leitor de tela: a linha da ficha já escreve "Prioridade". */
  rotuloOculto?: boolean;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [erro, definirErro] = useState(false);

  const gravar = useMutation({
    mutationFn: (escolhida: Prioridade) => {
      if (!api) throw new Error('sem sessão');
      return api.definirPrioridadeDaConversa({
        caminho: { id: conversaId },
        corpo: { prioridade: escolhida },
      });
    },
    onMutate: () => definirErro(false),
    onSuccess: () => {
      /*
       * A conversa E a fila. O degrau aparece nas duas, e invalidar só uma
       * deixaria a lista contradizendo o painel na mesma tela.
       */
      void cache.invalidateQueries({ queryKey: chaves.conversa(conversaId) });
      void cache.invalidateQueries({ queryKey: chaves.inbox() });
    },
    onError: () => definirErro(true),
  });

  return (
    <CampoEscolha
      {...(rotuloOculto ? { rotuloOculto: 'Prioridade' } : { rotulo: 'Prioridade' })}
      valor={prioridade ?? ''}
      opcoes={OPCOES}
      desabilitado={gravar.isPending || api === null}
      ajuda={gravar.isPending ? 'salvando…' : undefined}
      erro={
        erro
          ? 'Não foi possível mudar a prioridade. A conversa continua como estava — tente de novo.'
          : undefined
      }
      aoEscolher={(escolhida) => gravar.mutate(escolhida === '' ? null : (escolhida as Prioridade))}
    />
  );
}
